import type { GridApi } from 'ag-grid-community';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useMediaQuery } from 'src/hooks/useMediaQuery';

export const DRAWER_IDS = {
  INTERNAL_CHAT: 'INTERNAL_CHAT',
  NOTES: 'NOTES',
  DETAILS: 'DETAILS',
} as const;

export type DrawerId = (typeof DRAWER_IDS)[keyof typeof DRAWER_IDS];

const CONTEXT_BAR_STATE_KEY = 'assembly_context_bar_state';

interface PersistedContextBarState {
  drawerHistory: DrawerId[];
  open: boolean;
}

function loadPersistedState(): PersistedContextBarState | undefined {
  try {
    const raw = localStorage.getItem(CONTEXT_BAR_STATE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (typeof parsed !== 'object' || parsed === null) return undefined;

    const validIds = new Set<string>(Object.values(DRAWER_IDS));
    const drawerHistory = Array.isArray(parsed.drawerHistory)
      ? (parsed.drawerHistory as unknown[]).filter(
          (id): id is DrawerId => typeof id === 'string' && validIds.has(id),
        )
      : [];

    return {
      drawerHistory,
      open: typeof parsed.open === 'boolean' ? parsed.open : false,
    };
  } catch {
    return undefined;
  }
}

function savePersistedState(state: PersistedContextBarState): void {
  try {
    localStorage.setItem(CONTEXT_BAR_STATE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error('Failed to save context bar state to localStorage', e);
  }
}

export const DRAWER_LABELS: Record<DrawerId, string> = {
  [DRAWER_IDS.INTERNAL_CHAT]: 'Internal Chat',
  [DRAWER_IDS.NOTES]: 'Internal Notes',
  [DRAWER_IDS.DETAILS]: 'Details',
};

export const MOBILE_DRAWER_LABELS: Record<DrawerId, string> = {
  [DRAWER_IDS.INTERNAL_CHAT]: 'Chat',
  [DRAWER_IDS.NOTES]: 'Notes',
  [DRAWER_IDS.DETAILS]: 'Details',
};

export interface DrawerActionConfig {
  icon: string;
  label: string;
  actionType: 'create_note';
}

export const DRAWER_ACTION_CONFIGS: Partial<
  Record<DrawerId, DrawerActionConfig>
> = {
  [DRAWER_IDS.NOTES]: {
    icon: 'Plus',
    label: 'Create note',
    actionType: 'create_note',
  },
};

export interface ContextBarTarget {
  id: string;
  entityType: 'client' | 'company';
}

export type DrawerVisibilityConfig = Partial<Record<DrawerId, boolean>>;

const DEFAULT_DRAWER_VISIBILITY: Record<DrawerId, boolean> = {
  [DRAWER_IDS.INTERNAL_CHAT]: true,
  [DRAWER_IDS.NOTES]: true,
  [DRAWER_IDS.DETAILS]: true,
};

export interface ContextBarPageTransition {
  target: ContextBarTarget;
  drawerConfig?: DrawerVisibilityConfig;
  open?: boolean;
}

interface ContextBarState {
  target?: ContextBarTarget;
  drawerVisibilityConfig: Record<DrawerId, boolean>;
  drawerHistory: DrawerId[];
  open: boolean;
  userOpenPreference?: boolean;
  isLoading: boolean;
}

export interface ContextBarContextValue extends ContextBarState {
  transition: (transition: ContextBarPageTransition) => void;
  pushDrawerHistory: (drawerId: DrawerId) => void;
  getActiveDrawer: (hasAssistantTab: boolean) => DrawerId;
  setOpen: (value: boolean) => void;
  clearTarget: () => void;
  setContextBarLoading: (loading: boolean) => void;
  isMobileLayout: boolean;
  gridApi: GridApi | undefined;
  setGridApi: (api: GridApi | undefined) => void;
}

const defaultContextValue: ContextBarContextValue = {
  target: undefined,
  drawerVisibilityConfig: DEFAULT_DRAWER_VISIBILITY,
  drawerHistory: [],
  open: false,
  userOpenPreference: undefined,
  isLoading: false,
  transition: () => {},
  pushDrawerHistory: () => {},
  getActiveDrawer: () => DRAWER_IDS.INTERNAL_CHAT,
  setOpen: () => {},
  clearTarget: () => {},
  setContextBarLoading: () => {},
  isMobileLayout: false,
  gridApi: undefined,
  setGridApi: () => {},
};

export const ContextBarContext =
  createContext<ContextBarContextValue>(defaultContextValue);

const initialState: ContextBarState = {
  target: undefined,
  drawerVisibilityConfig: DEFAULT_DRAWER_VISIBILITY,
  drawerHistory: [],
  open: false,
  userOpenPreference: undefined,
  isLoading: false,
};

export const ContextBarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<ContextBarState>(() => {
    const persisted = loadPersistedState();
    if (!persisted) return initialState;
    return {
      ...initialState,
      drawerHistory: persisted.drawerHistory,
      userOpenPreference: persisted.open,
      open: persisted.open,
    };
  });
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);

  const isMobileLayout = useMediaQuery('sm', 'max');

  useEffect(() => {
    savePersistedState({
      drawerHistory: state.drawerHistory,
      open: state.userOpenPreference ?? false,
    });
  }, [state.drawerHistory, state.userOpenPreference]);

  const setOpen = useCallback(
    (value: boolean) =>
      setState((prev) => ({ ...prev, open: value, userOpenPreference: value })),
    [setState],
  );

  const clearTarget = useCallback(() => {
    setState((prev) => ({ ...prev, target: undefined }));
    setGridApi(undefined);
  }, [setState, setGridApi]);

  const setContextBarLoading = useCallback(
    (loading: boolean) => setState((prev) => ({ ...prev, isLoading: loading })),
    [setState],
  );

  const transition = useCallback(
    (transitionObj: ContextBarPageTransition) => {
      setState((prev) => {
        let nextOpen = false;
        if (!isMobileLayout) {
          if (transitionObj.open !== undefined) {
            nextOpen = transitionObj.open;
          } else if (prev.userOpenPreference !== undefined) {
            nextOpen = prev.userOpenPreference;
          }
        }

        return {
          ...prev,
          target: transitionObj.target,
          drawerVisibilityConfig: {
            ...DEFAULT_DRAWER_VISIBILITY,
            ...Object.fromEntries(
              Object.entries(transitionObj.drawerConfig ?? {}).filter(
                ([, v]) => v !== undefined,
              ),
            ),
          },
          open: nextOpen,
        };
      });
    },
    [isMobileLayout, setState],
  );

  const pushDrawerHistory = useCallback((drawerId: DrawerId) => {
    setState((prev) => {
      const filtered = prev.drawerHistory.filter((id) => id !== drawerId);
      return { ...prev, drawerHistory: [drawerId, ...filtered] };
    });
  }, []);

  const isDrawerValid = useCallback(
    (drawerId: DrawerId, hasAssistantTab: boolean): boolean => {
      if (!state.drawerVisibilityConfig[drawerId]) {
        return false;
      }
      if (drawerId === DRAWER_IDS.INTERNAL_CHAT && !hasAssistantTab) {
        return false;
      }
      return true;
    },
    [state.drawerVisibilityConfig],
  );

  const getActiveDrawer = useCallback(
    (hasAssistantTab: boolean): DrawerId => {
      for (const drawerId of state.drawerHistory) {
        if (isDrawerValid(drawerId, hasAssistantTab)) {
          return drawerId;
        }
      }
      return hasAssistantTab && state.drawerVisibilityConfig.INTERNAL_CHAT
        ? DRAWER_IDS.INTERNAL_CHAT
        : DRAWER_IDS.NOTES;
    },
    [
      state.drawerHistory,
      state.drawerVisibilityConfig.INTERNAL_CHAT,
      isDrawerValid,
    ],
  );

  const value = useMemo(
    () =>
      ({
        ...state,
        transition,
        pushDrawerHistory,
        getActiveDrawer,
        setOpen,
        clearTarget,
        setContextBarLoading,
        isMobileLayout,
        gridApi,
        setGridApi,
      }) satisfies ContextBarContextValue,
    [
      state,
      transition,
      pushDrawerHistory,
      getActiveDrawer,
      setOpen,
      clearTarget,
      setContextBarLoading,
      isMobileLayout,
      gridApi,
      setGridApi,
    ],
  );

  return (
    <ContextBarContext.Provider value={value}>
      {children}
    </ContextBarContext.Provider>
  );
};
